import { toPostStatusEnum } from '@/Components/posts/status';
import { ConnectionHandler, type MutationParameters, type RecordSourceSelectorProxy } from 'relay-runtime';
import type { ZnglyPostTypeEnum } from './mutations/__generated__/BatchPostMutation_Update_Mutation.graphql';
import { useRelayStore } from './useRelayStore';

export const postStatusUpdater = <TOperation extends MutationParameters['response']>(
  store: RecordSourceSelectorProxy<TOperation>,
  ids: string[],
  postType: ZnglyPostTypeEnum,
  status: string
) => {
  // update the status of the posts
  for (const id of ids) {
    const post = store.get(id);
    if (post) {
      post.setValue(toPostStatusEnum(status), 'status');
    }
  }

  const getFragment = useRelayStore.getState().getByKey;
  let regex = null;
  let relayFragment = {} as ReturnType<typeof getFragment>;

  if (postType === 'Catalog') {
    relayFragment = getFragment('ZnglyVideos_Pagination_Fragment');
    regex = /stati\":\[\"(.*?)\"\]/;
  } else if (postType === 'Znglist') {
    relayFragment = getFragment('Znglists_Pagination_Fragment');
    regex = /stati\":\[\"(.*?)\"\]/;
  } else if (postType === 'Hub') {
    relayFragment = getFragment('Hubs_Pagination_Fragment');
    regex = /statusIn\":\[\"(.*?)\"\]/;
  } else if (postType === 'HubPage') {
    relayFragment = getFragment('HubPages_Pagination_Fragment');
    regex = /statusIn\":\[\"(.*?)\"\]/;
  } else if (postType === 'Form') {
    relayFragment = getFragment('Forms_Pagination_Fragment');
    regex = /statusIn\":\[\"(.*?)\"\]/;
  } else if (postType === 'CrmList') {
    relayFragment = getFragment('CrmList_Pagination_Fragment');
    regex = /statusIn\":\[\"(.*?)\"\]/;
  }

  if (!relayFragment?.__id?.length || !regex) {
    return;
  }

  const nodeIds = ids;
  const updatedStatus = status.toUpperCase();

  for (const id of relayFragment.__id) {
    const connection = store.get(id);
    if (!connection) {
      continue;
    }

    // client:root:__ZnglyVideos_Pagination_Fragment_Connection_znglyVideos_connection(where:{"authorIn":null,"dateQuery":null,"mediaName":null,"orderby":null,"search":null,"stati":["PENDING","PUBLISH","HIDDEN","DRAFT"]})
    const stati = id.match(regex)?.[1]?.replaceAll('"', '')?.split(',') ?? [];

    const shouldAdd = stati.includes(updatedStatus); // this current query has our new status -> we should add the new status if it does not exist
    const shouldRemove = !stati.includes(updatedStatus); // this current query does not have our new status

    // from the data id, we want to get the arguments
    const edges = connection?.getLinkedRecords('edges');
    if (!edges?.length) {
      continue;
    }

    const foundNodeIds = [] as string[];
    for (const edge of edges) {
      const node = edge?.getLinkedRecord('node');

      if (!node || !nodeIds?.includes?.(node?.getDataID())) {
        continue;
      }

      const currNodeId = node?.getDataID();
      if (!currNodeId) {
        continue;
      }

      foundNodeIds.push(currNodeId);

      if (shouldRemove) {
        // remove the node from the connection
        ConnectionHandler.deleteNode(connection, currNodeId);
      }
    }

    // if not all nodes were found and we should add, we should add the nodes
    if (shouldAdd && foundNodeIds.length !== nodeIds.length) {
      const diff = nodeIds.filter((x) => !foundNodeIds.includes(x));

      for (const nodeId of diff) {
        const node = store.get(nodeId);
        if (node) {
          const newEdge = ConnectionHandler.createEdge(store, connection, node, `${relayFragment.__typename}Edge`);
          ConnectionHandler.insertEdgeBefore(connection, newEdge);
        }
      }
    }
  }
};
